<template>
	<div class="scan-page w-100 p-3" style="height: 75vh;overflow-y: auto;">

		<!-- PANNELLO SCAN INFO -->
		<section class="scan-panel p-3">
			<div class="d-flex flex-justify-between">
				<h3>{{ $t("Network scan") }}</h3>
				<div class="d-flex flex-column flex-align-end">
					<div class="float-right row">
						<button @click="startNetworkScan(true)" :title="$t('Run')" class="button flat-button" :disabled="status == $ResultValue.Running">
							<svg width="20" height="20" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
								<path d="M5 5.274c0-1.707 1.826-2.792 3.325-1.977l12.362 6.726c1.566.853 1.566 3.101 0 3.953L8.325 20.702C6.826 21.518 5 20.432 5 18.726V5.274Z" fill="#212121" />
							</svg>
						</button>
						<button @click="stopNetworkScan" :title="$t('Stop')" class="button  flat-button" :disabled="status != $ResultValue.Running">
							<svg width="20" height="20" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
								<path d="M4.75 3A1.75 1.75 0 0 0 3 4.75v14.5c0 .966.784 1.75 1.75 1.75h14.5A1.75 1.75 0 0 0 21 19.25V4.75A1.75 1.75 0 0 0 19.25 3H4.75Z" fill="#212121" />
							</svg>
						</button>
					</div>
					<ClientMessageView ref="FMV" :commands="['getnetworkdevices', 'startnetworkscan', 'stopnetworkscan']" :inDialogActions="false" />
				</div>
			</div>

			<div class="scan-metrics">
				<div class="metric">
					<div class="k">{{ $t("Started") }}</div>
					<div class="v">{{ scan?.scanStartedAt ? dateHelper.toSimpleDateString(scan.scanStartedAt, locale) : '—' }}</div>
				</div>
				<div class="metric">
					<div class="k">{{ $t("Completed") }}</div>
					<div class="v">{{ scan?.scanCompletedAt ? dateHelper.toSimpleDateString(scan.scanCompletedAt, locale) : '—' }}</div>
				</div>
				<div class="metric">
					<div class="k">{{ $t("Duration") }}</div>
					<div class="v">{{ scan?.durationSeconds ? (Math.round(Number(scan.durationSeconds)) + " s") : '—' }}</div>
				</div>
				<div class="metric">
					<div class="k">{{ $t("Networks") }}</div>
					<div class="v">{{ scan?.summary?.networksScanned ?? '—' }}</div>
				</div>
				<div class="metric">
					<div class="k">{{ $t("Devices") }}</div>
					<div class="v">{{ scan?.summary?.totalDevices ?? '—' }}</div>
				</div>
			</div>
		</section>

		<div class="w-100 text-center" v-if="!scan">
			<span class="mif-spinner2 ani-spin mif-3x"></span>
		</div>
		<NoDataAvailable v-else-if="scan.networks.length == 0" :message="$t('No network available')" />

		<div class="content-grid h-100 overflow" v-else>
			<!-- SINISTRA: RETI -->
			<section class="left h-100 overflow p-3 bg-white">
				<div class="section-head">
					<h4>{{ $t("Networks") }}</h4>
				</div>

				<div class="network-list">
					<button v-for="(n, idx) in networks" :key="n.network + idx" class="network-card" :class="{ active: idx === selectedNetworkIndex }" @click="selectNetwork(idx)">
						<div class="row">
							<div class="net">{{ n.network }}</div>
							<div class="count">{{ n.deviceCount }} {{ $t("devices") }}</div>
						</div>

						<div class="sub">
							<div class="iface">{{ n.interfaceName }}</div>
						</div>

						<div class="desc muted">{{ n.interfaceDescription }}</div>
					</button>

					<div v-if="networks.length === 0" class="empty">
						{{ $t("No network available") }}
					</div>
				</div>
			</section>

			<!-- DESTRA: DEVICES -->
			<section class="right h-100 overflow p-3 bg-white">
				<div class="section-head">
					<div>
						<h4>{{ $t("Devices") }}</h4>
						<div class="muted" v-if="selectedNetwork">
							{{ selectedNetwork.network }} • {{ selectedNetwork.interfaceName }}
						</div>
					</div>

					<div class="row">
						<div class="filter-right">
							<label class="text-bold">{{ $t("Search") }}</label>
							<input v-model="deviceSearch" type="text" data-role="input" data-clear-button="true">
						</div>

						<div class="filter-right">
							<label class="text-bold">{{ $t("Iperius ONE Agent") }}</label>
							<select class="select text-small w-auto px-2" v-model="hasAgent">
								<option :value="-1">{{ $t("Show all") }}</option>
								<option :value="true">{{ $t("Installed") }}</option>
								<option :value="false">{{ $t("Not installed") }}</option>
							</select>
						</div>

					</div>
				</div>

				<div class="table-wrap">
					<table class="bg-white border table striped compact overflow">
						<thead>
							<tr>
								<th>IP</th>
								<th>{{ $t("Hostname") }}</th>
								<th>{{ $t("MAC") }}</th>
								<th class="num">{{ $t("RTT (ms)") }}</th>
								<th>{{ $t("Discovered") }}</th>
							</tr>
						</thead>

						<tbody>
							<tr v-for="d in filteredDevices" :key="d.ip + (d.mac || '')">
								<td>{{ d.ip }}</td>
								<td class="d-flex flex-align-center">
									<span>{{ d.hostname || '—' }}</span>
									<IperiusONEIcon v-if="d.hasAgent" class="ml-1" :pc="{ id: 'pc_from_net_scan', backup_license_status: 1, notConnectedDefaultMessage: 'Iperius ONE installed', iconHeight: 20 }" />
								</td>
								<td>{{ d.mac || '—' }}</td>
								<td class="num">{{ d.responseTimeMs }}</td>
								<td>{{ dateHelper.toSimpleDateString(d.discoveredAt, locale) }}</td>

							</tr>

							<tr v-if="filteredDevices.length === 0">
								<td colspan="8" class="empty-row"> {{ $t("No devices matching the filters.") }} </td>
							</tr>
						</tbody>
					</table>
				</div>
			</section>
		</div>

	</div>
</template>

<script>
import { ClientMessage, DateHelper } from '../../utils/objects';
import { assignProperties, waitSeconds } from '../../utils/utilitiesmodule';
import ClientMessageView from '../dialogs/view/ClientMessageView.vue';
import IperiusONEIcon from '../utils/IperiusONEIcon.vue';
import NoDataAvailable from '../utils/NoDataAvailable.vue';

export default {
	name: "NetworkScan",
	components: {
		NoDataAvailable,
		ClientMessageView,
		IperiusONEIcon
	},

	data() {
		return {
			scan: null,
			status: this.$ResultValue.NeverExecuted,


			selectedNetworkIndex: 0,
			selectedNetwork: null,

			deviceSearch: "",
			hasAgent: -1,
			dateHelper: DateHelper,

			locale: this.$i18n?.global?.locale?.value ?? this.$i18n?.locale ?? "en",
		};
	},

	computed: {
		networks() {
			return this.scan?.networks ?? [];
		},

		devices() {
			return this.selectedNetwork?.devices ?? [];
		},

		filteredDevices() {
			const q = this.deviceSearch.trim().toLowerCase();
			var f1, f2;
			return this.devices
				//.filter((d) => (this.hasAgent? d.hasAgent:true))
				.filter((d) => {

					if (d.hasAgent) {
						//Simulo che è connesso per visualizzare l'icona colorata
						this.$root.connectionsStates['pc_from_net_scan'] = true;
					}

					f1 = this.hasAgent == -1 || this.hasAgent == d.hasAgent;

					if (!q) {
						return f1;
					}

					const hay = [d.ip, d.hostname, d.mac]
						.map((x) => (x ?? "").toString().toLowerCase())
						.join(" ");
					f2 = hay.includes(q);

					return f1 && f2;
				})
				.slice()
				.sort((a, b) =>
					(a.ip || "").localeCompare(b.ip || "", undefined, { numeric: true })
				);
		},

	},
	watch: {
		status: {
			handler(value) {
				var clientMessage = null;
				switch (value) {
					case this.$ResultValue.Running:
						clientMessage = ClientMessage.wait("Starting network scan...", null);
						break;
					case this.$ResultValue.Error:
						clientMessage = ClientMessage.error("An error occurred during network scan.", null);
						break;
					case this.$ResultValue.OK:
						clientMessage = ClientMessage.success("Network scan completed successfully", null);
						break;
					case this.$ResultValue.SomeWarnings:
						clientMessage = ClientMessage.warning("No network found");
						break;
					case this.$ResultValue.Aborted:
						clientMessage = ClientMessage.info("Network scan stopped by user");
						break;
				}

				if (clientMessage != null)
					this.$refs.FMV.setClientMessage(clientMessage);
			}
		}
	},

	methods: {

		async startNetworkScan(manual) {
			var isPopulated = this.scan && this.scan.networks && this.scan.networks.length == 0;

			//Caso 1: l'utente ha riaperto la scheda del network scan
			if (!manual && isPopulated) {
				return;
			}

			//Caso 2: l'utente ha avviato la scansione manuale
			if (manual && this.status != this.$ResultValue.Running) {
				this.status = this.$ResultValue.Running;
				await this.$root.socket("startnetworkscan");
			}

			//Caso 3 : Qui so che c'è una scansione in corso
			//quindi ne devo attendere il risultato (per un massimo di 5 minuti)
			for (var s = 1; s <= 300; s++) {
				if (this.status == this.$ResultValue.Running) {
					await waitSeconds(1);
					continue;
				}

				if (this.status == this.$ResultValue.Error) {
					break;
				}

				//this.status == this.$ResultValue.OK
				this.status = this.$ResultValue.Running;
				await this.$root.socket("getnetworkdevices").then(async res => {
					await this.setScan(res, true);
				});
				return;
			}

			this.setScan(null, true);
		},

		stopNetworkScan() {
			this.$root.socket("stopnetworkscan").then(_ => {
				this.status = this.$ResultValue.Aborted;
			});
		},

		async setScan(newScan, refresh) {
			this.selectedNetwork = null;
			this.selectedNetworkIndex = -1;
			if (!newScan || newScan.networks == null) {
				this.status = this.$ResultValue.Error;
				this.scan = {
					"scanStartedAt": null,
					"scanCompletedAt": null,
					"durationSeconds": null,
					"summary": {
						"totalDevices": 0,
						"networksScanned": 0
					},
					"networks": []
				};


				return;
			}

			if (refresh && this.scan != null) {
				assignProperties(newScan, this.scan);
			} else {
				this.scan = newScan;
			}


			if (this.scan.networks.length > 0) {
				this.selectedNetwork = this.scan.networks[0];
				this.selectedNetworkIndex = 0;
				this.status = this.$ResultValue.OK;

			} else {
				this.status = this.$ResultValue.SomeWarnings;
			}
		},

		selectNetwork(idx) {
			this.selectedNetworkIndex = idx;
			this.selectedNetwork = this.scan.networks[idx];
		},

		setScanData(scanObj) {
			this.scan = scanObj;
			this.selectedNetworkIndex = 0;
		},
	},
};
</script>

<style scoped>
.scan-page {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.scan-panel {
	border: 1px solid #ddd;
	border-radius: 5px;
	background: #fff;
}


.scan-metrics {
	display: grid;
	grid-template-columns: repeat(5, minmax(0, 1fr));
	gap: 10px;
	margin-top: 10px;
}

.metric {
	border: 1px solid #eee;
	border-radius: 10px;
	padding: 10px;
}

.metric .k {
	font-size: 12px;
	opacity: 0.7;
}

.metric .v {
	font-weight: 600;
	margin-top: 4px;
}

.content-grid {
	display: grid;
	grid-template-columns: 420px 1fr;
	gap: 12px;
}

.left,
.right {
	border: 1px solid #ddd;
	border-radius: 5px;
}

.section-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 10px;
}

.tools {
	display: flex;
	align-items: center;
	gap: 10px;
}

.tools input {
	width: 100%;
	max-width: 420px;
	padding: 8px 10px;
	border: 1px solid #ddd;
	border-radius: 10px;
}

.toggle {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 13px;
	user-select: none;
}

.network-list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.network-card {
	text-align: left;
	border: 1px solid #eee;
	border-radius: 12px;
	padding: 10px;
	background: #fff;
	cursor: pointer;
}

.network-card.active {
	border-color: #999;
}

.network-card .row {
	display: flex;
	justify-content: space-between;
	gap: 10px;
}

.network-card .net {
	font-weight: 700;
}

.network-card .count {
	font-size: 13px;
	opacity: 0.8;
}

.network-card .sub {
	display: flex;
	justify-content: space-between;
	gap: 10px;
	margin-top: 6px;
}

.network-card .desc {
	margin-top: 6px;
}

.table-wrap {
	overflow: auto;
	border: 1px solid #eee;
}

.devices-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}

.devices-table th,
.devices-table td {
	padding: 10px;
	border-bottom: 1px solid #eee;
	white-space: nowrap;
}

.devices-table th {
	text-align: left;
	font-weight: 700;
	position: sticky;
	top: 0;
	background: #fff;
}

.num {
	text-align: right;
}

.mono {
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}


.pill {
	display: inline-block;
	padding: 3px 8px;
	border-radius: 999px;
	font-size: 12px;
	border: 1px solid #ddd;
}

.pill.ok {
	border-color: #7bd389;
}

.pill.ko {
	border-color: #eb5757;
}

.device-panel {
	margin-top: 12px;
	border: 1px solid #eee;
	padding: 10px;
}

.panel-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.panel-head .x {
	border: 0;
	background: transparent;
	cursor: pointer;
	font-size: 16px;
}

.panel-body {
	margin-top: 10px;
	display: grid;
	gap: 6px;
}

.panel-body .k {
	display: inline-block;
	width: 110px;
	opacity: 0.7;
}

.empty,
.empty-row {
	padding: 14px;
	opacity: 0.7;
}

.empty-row {
	text-align: center;
}

.muted {
	opacity: 0.7;
}

@media (max-width: 1100px) {
	.content-grid {
		grid-template-columns: 1fr;
	}

	.scan-metrics {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
</style>
